import { FormProvider } from 'react-hook-form'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Stack, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { t, Trans } from '@lingui/macro'
import { ConsultationType } from '@/core/models/consultation.model'
import LoadingButton from '@mui/lab/LoadingButton'
import { bindMenu, bindTrigger } from 'material-ui-popup-state'
import PopupState from 'material-ui-popup-state'
import AutocompleteInputController from '@/components/ui/inputs/AutocompleteInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import {
  usePreConsultationForm,
  usePreConsultationFormContext,
  useReasonOptions,
  useSpecialityField,
  useWaitingRoomOptions,
} from '../model/usePreConsultationForm'

type PreConsultationModalProps = {
  open: boolean
  onClose: () => void
  onSelect: (waitingRoomId: string | null) => void
  patientId?: string
}

export function PreConsultationModal({ open, onClose, onSelect, patientId }: PreConsultationModalProps) {
  const { rhForm, typeValue, handleSubmit, isCreating } = usePreConsultationForm({ open, patientId, onSelect })

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Trans>Start the pre-consultation</Trans>
        <Tooltip title={t`Close`}>
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...rhForm}>
          <form onSubmit={rhForm.handleSubmit(handleSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ConsultationTypeField />
              </Grid>
              {typeValue === ConsultationType.Classic && (
                <Grid item xs={12}>
                  <WaitingRoomField />
                </Grid>
              )}
              <Grid item xs={12}>
                <SpecialityField />
              </Grid>
              <Grid item xs={12}>
                <ConsultationReasonField />
              </Grid>
            </Grid>
            <Stack direction="row" justifyContent="end" mt={3} spacing={1}>
              <Button onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <LoadingButton loading={isCreating} type="submit" variant="contained">
                <Trans>Start the consultation</Trans>
              </LoadingButton>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}

function ConsultationTypeField() {
  const { control } = usePreConsultationFormContext()

  return (
    <SelectInputController
      control={control}
      fullWidth
      label={t`Consultation type`}
      name="type"
      options={[
        { label: t`Consultation classic`, value: ConsultationType.Classic },
        { label: t`Consultation emergency`, value: ConsultationType.Emergency },
        { label: t`Local consultation`, value: ConsultationType.Local },
      ]}
    />
  )
}

function WaitingRoomField() {
  const { control } = usePreConsultationFormContext()
  const waitingRoomOptions = useWaitingRoomOptions()

  return <SelectInputController control={control} fullWidth label={t`Waiting room`} name="waitingRoomId" options={waitingRoomOptions} />
}

function SpecialityField() {
  const { control } = usePreConsultationFormContext()
  const { options, searchSpeciality } = useSpecialityField()
  return (
    <AutocompleteInputController
      control={control}
      freeSolo={false}
      label={t`Speciality`}
      name="speciality"
      onInputChange={searchSpeciality}
      options={options}
    />
  )
}

function ConsultationReasonField() {
  const { control, setValue } = usePreConsultationFormContext()
  const reasonOptions = useReasonOptions()

  return (
    <TextInputController
      control={control}
      fullWidth
      label={t`Consultation reason`}
      name="reason"
      InputProps={{
        endAdornment: (
          <PopupState variant="popover" popupId="reason-options">
            {(popupState) => (
              <>
                <IconButton size="small" {...bindTrigger(popupState)}>
                  •••
                </IconButton>
                <Menu {...bindMenu(popupState)} disablePortal anchorReference="anchorEl">
                  {reasonOptions.map((option, index) => (
                    <MenuItem
                      key={`${option}-${index}`}
                      onClick={() => {
                        setValue('reason', option)
                        popupState.close()
                      }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </PopupState>
        ),
      }}
    />
  )
}
